import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { updatePricingStrategy } from '@/api/generated/sdk.gen';
import type { PricingRule, PricingStrategy } from '@/lib/pricing';
import { RULE_TYPE_LABELS, defaultParams, type PricingRuleType } from '@/lib/pricingRules';
import { messageFromError } from '@/lib/api';
import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { IconButton } from '@/components/ui/icon-button';
import { RuleBody } from './RuleForms';

interface EditableRule {
  key: string; // stable client key for React + reorder
  type: PricingRuleType;
  enabled: boolean;
  params: Record<string, unknown>;
}

const RULE_TYPE_OPTIONS = (Object.keys(RULE_TYPE_LABELS) as PricingRuleType[]).map((t) => ({
  value: t,
  label: RULE_TYPE_LABELS[t],
}));

let keySeq = 0;
const nextKey = () => `new-${keySeq++}`;

function toEditable(rules: PricingRule[]): EditableRule[] {
  return [...rules]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map((r, i) => ({
      key: r.id ?? `seed-${i}`,
      type: r.type,
      enabled: r.enabled !== false,
      params: (r.params ?? {}) as Record<string, unknown>,
    }));
}

// The PUT body shape: position re-numbered from order, id omitted (the server
// reassigns on replace, per the OpenAPI contract).
function toWireRules(rules: EditableRule[]): PricingRule[] {
  return rules.map((r, i) => ({
    type: r.type,
    position: i,
    enabled: r.enabled,
    params: r.params,
  }));
}

function snapshot(rules: EditableRule[]): string {
  return JSON.stringify(rules.map((r) => ({ type: r.type, enabled: r.enabled, params: r.params })));
}

/**
 * RulesEditor — the editable Rules section of a strategy. Owns the rule list
 * (add / remove / reorder / enable) and renders each rule's typed params form.
 * Save PUTs the whole strategy (rules replace transactionally), carrying the
 * strategy's current name + default_min_stay through unchanged.
 */
export function RulesEditor({ strategy }: { strategy: PricingStrategy }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [rules, setRules] = useState<EditableRule[]>(() => toEditable(strategy.rules));
  const [addType, setAddType] = useState<PricingRuleType>('seasonal');
  const [error, setError] = useState<string | null>(null);
  // Bumped on Reset to remount every rule form. Each form seeds its inputs from
  // `params` exactly once (useState initializer), so without a key change Reset
  // would revert the parent model while the form inputs kept their edited
  // drafts — a display-vs-data mismatch. The token is part of each form's key.
  const [resetToken, setResetToken] = useState(0);
  const baselineRef = useRef<string>(snapshot(toEditable(strategy.rules)));

  const dirty = snapshot(rules) !== baselineRef.current;

  const mut = useMutation({
    mutationFn: async () => {
      return updatePricingStrategy({
        throwOnError: true,
        path: { id: strategy.id },
        body: {
          name: strategy.name,
          default_min_stay: strategy.default_min_stay,
          rules: toWireRules(rules),
        },
      });
    },
    onSuccess: () => {
      baselineRef.current = snapshot(rules);
      qc.invalidateQueries({ queryKey: ['pricing-strategies'] });
      toast({ kind: 'success', title: 'Rules saved', message: strategy.name });
    },
    onError: (err) => setError(messageFromError(err)),
  });

  function update(key: string, patch: Partial<EditableRule>) {
    setRules((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= rules.length) return;
    setRules((rs) => {
      const next = [...rs];
      const a = next[i];
      const b = next[j];
      if (!a || !b) return rs;
      next[i] = b;
      next[j] = a;
      return next;
    });
  }
  function addRule() {
    setRules((rs) => [
      ...rs,
      { key: nextKey(), type: addType, enabled: true, params: defaultParams(addType) },
    ]);
  }

  return (
    <section className="mt-6 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-muted">Rules</h2>
        <div className="flex items-end gap-2">
          <Select
            label=""
            aria-label="Rule type to add"
            options={RULE_TYPE_OPTIONS}
            value={addType}
            onChange={(e) => setAddType(e.target.value as PricingRuleType)}
            className="min-w-[12rem]"
          />
          <Button type="button" variant="secondary" size="sm" onClick={addRule}>
            <Plus className="h-4 w-4" />
            Add rule
          </Button>
        </div>
      </div>

      {rules.length === 0 ? (
        <p className="text-sm text-muted">
          No rules yet. This strategy applies only its base rate and default min-stay. Add a rule
          above to start adjusting prices and restrictions.
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {rules.map((r, i) => (
            <li
              key={`${r.key}:${resetToken}`}
              className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-4"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="font-medium text-sm">{RULE_TYPE_LABELS[r.type]}</span>
                  <Checkbox
                    label="Enabled"
                    checked={r.enabled}
                    onChange={(e) => update(r.key, { enabled: e.target.checked })}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <IconButton
                    label="Move up"
                    size="sm"
                    variant="ghost"
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    label="Move down"
                    size="sm"
                    variant="ghost"
                    disabled={i === rules.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </IconButton>
                  <IconButton
                    label="Remove rule"
                    size="sm"
                    variant="ghost"
                    onClick={() => setRules((rs) => rs.filter((x) => x.key !== r.key))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </IconButton>
                </div>
              </div>
              <div className={r.enabled ? '' : 'opacity-55'}>
                <RuleBody
                  type={r.type}
                  params={r.params}
                  onChange={(next) => update(r.key, { params: next })}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center gap-2">
        <Button
          type="button"
          variant="primary"
          loading={mut.isPending}
          disabled={!dirty || mut.isPending}
          onClick={() => {
            setError(null);
            mut.mutate();
          }}
        >
          Save rules
        </Button>
        {dirty && (
          <Button
            type="button"
            variant="secondary"
            disabled={mut.isPending}
            onClick={() => {
              const base = toEditable(strategy.rules);
              setRules(base);
              baselineRef.current = snapshot(base);
              setError(null);
              setResetToken((t) => t + 1); // remount forms so their drafts re-seed
            }}
          >
            Reset
          </Button>
        )}
      </div>
    </section>
  );
}
